<script lang="ts" setup>
  import { computed, onMounted, ref, useTemplateRef } from 'vue';
  import { userAction } from '../../../../shared/observability/userAction.ts';
  import { InputField } from '../../../../shared/ui/input-field';

  const props = defineProps<{ existingFeedback?: null | string; error: string; textError?: string }>();

  defineEmits<{
    close: [];
    submit: [feedback: string];
  }>();

  const feedback = ref(props.existingFeedback ?? '');
  const hasExistingFeedback = computed(() => !!props.existingFeedback);

  const dialog = useTemplateRef<HTMLDialogElement>('dialog');
  onMounted(() => dialog.value?.showModal());
  const close = () => dialog.value?.close();
</script>

<template>
  <dialog
    ref="dialog"
    :data-faro-user-action-name="userAction.gameOverCancelFeedback"
    aria-describedby="feedback-modal-text"
    aria-labelledby="feedback-modal-title"
    @click.self="close"
    @close="$emit('close')"
  >
    <div class="modal-content">
      <h2 id="feedback-modal-title">{{ hasExistingFeedback ? 'Update Feedback' : 'Send Feedback' }}</h2>
      <p id="feedback-modal-text">
        {{
          hasExistingFeedback
            ? 'Want to change something in your feedback?'
            : 'Any new features or changes you want to see in songstar?'
        }}
      </p>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <InputField v-slot="field" :error="textError">
        <textarea
          v-model="feedback"
          v-bind="field"
          aria-labelledby="feedback-modal-title"
          autofocus
          class="feedback-textarea"
          maxlength="2000"
          placeholder="Write your feedback here..."
          rows="4"
        ></textarea>
      </InputField>

      <div class="modal-actions">
        <button
          :data-faro-user-action-name="userAction.gameOverCancelFeedback"
          class="cancel-button"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <button
          :data-faro-user-action-name="userAction.gameOverSubmitFeedback"
          :disabled="!feedback.trim() || feedback === existingFeedback"
          class="submit-button"
          type="button"
          @click="$emit('submit', feedback)"
        >
          {{ hasExistingFeedback ? 'Update Feedback' : 'Submit Feedback' }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
  dialog {
    background: var(--color-surface);
    border: none;
    border-radius: var(--radius-card);
    box-shadow: 0 10px 25px var(--color-shadow);
    color: inherit;
    margin: 1rem auto auto;
    max-height: calc(100dvh - 2rem);
    max-width: min(32rem, 100% - 2rem);
    padding: 0;
    width: 100%;

    &::backdrop {
      background: var(--color-backdrop);
    }
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
  }

  h2 {
    color: var(--color-primary);
    letter-spacing: 0.05em;
    margin: 0;
    text-transform: uppercase;
  }

  p {
    color: var(--color-text-muted);
    margin: 0;
  }

  .error-message {
    background: color-mix(in srgb, var(--color-error), transparent 90%);
    border-radius: var(--radius-control);
    color: var(--color-error);
    font-size: var(--font-size-sm);
    padding: 0.75rem 1rem;
  }

  .feedback-textarea {
    border: 2px solid var(--color-border-strong);
    border-radius: var(--radius-control);
    font: inherit;
    outline: none;
    padding: 1rem;
    resize: none;
    transition: border-color 0.2s;
    width: 100%;

    &::placeholder {
      color: var(--color-text-muted);
    }

    &:focus {
      border-color: var(--color-primary);
    }

    &[aria-invalid='true'] {
      border-color: var(--color-error);
    }
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
  }

  button {
    border: none;
    border-radius: var(--radius-control);
    cursor: pointer;
    flex: 1 1 0;
    font: inherit;
    font-weight: var(--font-weight-strong);
    min-height: var(--size-touch-target);
    padding: 0.75rem 1rem;
    transition: all 0.2s;
    white-space: nowrap;

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }
  }

  .cancel-button {
    background: var(--color-button-subtle);
    color: var(--color-text-muted);

    &:hover {
      background: var(--color-button-subtle-hover);
    }
  }

  .submit-button {
    background: var(--color-primary);
    color: var(--color-on-primary);

    &:disabled {
      background: var(--color-disabled);
      cursor: not-allowed;
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }
  }
</style>
